import { useCallback, useEffect, useRef } from "react";

const EDGE_THRESHOLD = 20; // px from left edge to start open gesture
const VELOCITY_THRESHOLD = 0.5; // px/ms to trigger snap
const DISTANCE_RATIO = 0.3; // 30% of sidebar width to trigger snap

type UseMobileSidebarSwipeOptions = {
    isOpen: boolean;
    onOpen: () => void;
    onClose: () => void;
    /** Ref to the sidebar panel — used for hit-testing and direct style updates during drag */
    sidebarRef: React.RefObject<HTMLDivElement | null>;
    /** Ref to the visual backdrop — opacity is updated directly during drag */
    backdropRef: React.RefObject<HTMLDivElement | null>;
    /** Width of the sidebar as a percentage of viewport width */
    sidebarWidthVw: number;
    enabled: boolean;
};

/**
 * Swipe-to-open / swipe-to-close for the mobile sidebar.
 *
 * All drag-phase DOM updates go through element.style directly — zero React
 * state updates, zero re-renders during the gesture. React state is only
 * touched at touchend to commit the open/close decision.
 */
export function useMobileSidebarSwipe({
    isOpen,
    onOpen,
    onClose,
    sidebarRef,
    backdropRef,
    sidebarWidthVw,
    enabled,
}: UseMobileSidebarSwipeOptions): void {
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const touchStartTime = useRef(0);
    const isTracking = useRef(false);
    const directionLocked = useRef(false);
    const isHorizontal = useRef(false);
    const isDragging = useRef(false);
    const lastOffset = useRef(0);
    const rafId = useRef(0);
    // Computed fresh at each touch-start so orientation changes are handled
    const sidebarWidthPx = useRef(0);

    // Ref-copy so event handlers always read the latest value without re-binding
    const isOpenRef = useRef(isOpen);
    isOpenRef.current = isOpen;

    const applyDragStyles = useCallback(
        (deltaX: number) => {
            const sidebar = sidebarRef.current;
            const backdrop = backdropRef.current;
            if (!sidebar) return;

            const width = sidebarWidthPx.current;
            let translateX: number;
            let progress: number; // 0 = fully closed, 1 = fully open

            if (isOpenRef.current) {
                // Closing: deltaX is negative (0 → -width)
                translateX = Math.min(0, Math.max(deltaX, -width));
                progress = 1 + translateX / width;
            } else {
                // Opening: deltaX is positive (0 → width)
                const clamped = Math.max(0, Math.min(deltaX, width));
                translateX = -width + clamped;
                progress = clamped / width;
            }

            sidebar.style.transform = `translate3d(${translateX}px, 0, 0)`;
            sidebar.style.transitionProperty = "none";
            sidebar.style.visibility = "visible";

            if (backdrop) {
                backdrop.style.opacity = `${Math.max(0, Math.min(1, progress))}`;
                backdrop.style.transitionProperty = "none";
            }
        },
        [sidebarRef, backdropRef],
    );

    const clearDragStyles = useCallback(() => {
        const sidebar = sidebarRef.current;
        const backdrop = backdropRef.current;
        if (sidebar) {
            sidebar.style.transform = "";
            sidebar.style.transitionProperty = "";
            sidebar.style.visibility = "";
        }
        if (backdrop) {
            backdrop.style.opacity = "";
            backdrop.style.transitionProperty = "";
        }
    }, [sidebarRef, backdropRef]);

    const handleTouchStart = useCallback(
        (e: TouchEvent) => {
            const touch = e.touches[0];
            if (!touch) return;

            if (!isOpenRef.current) {
                if (touch.clientX > EDGE_THRESHOLD) return;
            } else {
                if (!sidebarRef.current?.contains(e.target as Node)) return;
            }

            // Compute sidebar width fresh so orientation changes are handled
            sidebarWidthPx.current = (window.innerWidth * sidebarWidthVw) / 100;

            touchStartX.current = touch.clientX;
            touchStartY.current = touch.clientY;
            touchStartTime.current = Date.now();
            isTracking.current = true;
            directionLocked.current = false;
            isHorizontal.current = false;
        },
        [sidebarRef, sidebarWidthVw],
    );

    const handleTouchMove = useCallback(
        (e: TouchEvent) => {
            if (!isTracking.current) return;

            const touch = e.touches[0];
            if (!touch) return;

            const deltaX = touch.clientX - touchStartX.current;
            const deltaY = touch.clientY - touchStartY.current;

            if (!directionLocked.current) {
                if (Math.abs(deltaX) < 5 && Math.abs(deltaY) < 5) return;
                directionLocked.current = true;
                isHorizontal.current = Math.abs(deltaX) > Math.abs(deltaY);
                if (!isHorizontal.current) {
                    isTracking.current = false;
                    return;
                }
            }

            if (!isHorizontal.current) return;

            e.preventDefault();
            isDragging.current = true;
            lastOffset.current = deltaX;

            // Direct DOM update — no React re-render
            applyDragStyles(deltaX);
        },
        [applyDragStyles],
    );

    const handleTouchEnd = useCallback(() => {
        if (!isTracking.current || !isDragging.current) {
            isTracking.current = false;
            isDragging.current = false;
            lastOffset.current = 0;
            return;
        }

        isTracking.current = false;
        isDragging.current = false;

        const offset = lastOffset.current;
        lastOffset.current = 0;

        const width = sidebarWidthPx.current;
        const elapsed = Date.now() - touchStartTime.current;
        const velocity = Math.abs(offset) / Math.max(elapsed, 1);
        const threshold = width * DISTANCE_RATIO;

        let shouldChange = false;
        if (!isOpenRef.current) {
            // Opening: only trigger on rightward (positive) swipe
            shouldChange = offset > threshold || (offset > 0 && velocity > VELOCITY_THRESHOLD);
        } else {
            // Closing: only trigger on leftward (negative) swipe
            shouldChange = offset < -threshold || (offset < 0 && velocity > VELOCITY_THRESHOLD);
        }

        // Re-enable CSS transitions for the snap animation
        const sidebar = sidebarRef.current;
        const backdrop = backdropRef.current;
        if (sidebar) sidebar.style.transitionProperty = "";
        if (backdrop) backdrop.style.transitionProperty = "";

        if (shouldChange) {
            if (!isOpenRef.current) {
                onOpen();
            } else {
                onClose();
            }
            // Clear remaining inline overrides after React re-renders.
            // The rAF ensures we run after the browser has applied the new
            // CSS-driven transform from React's state update.
            rafId.current = requestAnimationFrame(() => {
                clearDragStyles();
            });
        } else {
            // Snap back to current state — clear inline styles
            clearDragStyles();
        }
    }, [onOpen, onClose, clearDragStyles, sidebarRef, backdropRef]);

    useEffect(() => {
        if (!enabled) return;

        document.addEventListener("touchstart", handleTouchStart, { passive: true });
        document.addEventListener("touchmove", handleTouchMove, { passive: false });
        document.addEventListener("touchend", handleTouchEnd, { passive: true });

        return () => {
            cancelAnimationFrame(rafId.current);
            document.removeEventListener("touchstart", handleTouchStart);
            document.removeEventListener("touchmove", handleTouchMove);
            document.removeEventListener("touchend", handleTouchEnd);
        };
    }, [enabled, handleTouchStart, handleTouchMove, handleTouchEnd]);
}
